import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import { useMemo } from 'react'
import stringSimilarity from 'string-similarity'

import { useDateFilter } from '../../../dateFilter'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface IInventorySummaryType {
  getCustomizedReport: {
    nodes: {
      reportResult: {
        summaryMap: Record<
          string,
          {
            item: string
            itemType: string
          }
        >
        tableData: {
          item: string
          itemType: string
        }[]
      }
    }[]
  }
}

const query = gql`
  query CorporateInventorySummary($iInputParams: JSON!) {
    getCustomizedReport(
      iCustomizedReportName: "CORPORATE_INVENTORY_TABLE_V2"
      iInputParams: $iInputParams
    ) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }
  }
`

export const bbbCorporateInventoryConfigs = {
  itemName: 'string',
  displayParentCategoryName: 'string',
  variance: 'price',
  variancePercent: 'percent',
  varianceSystemwideAvg: 'price',
  '<%- JSON(allStoreColumns) %>': 'price',
} as const

const useBbbCorporateInventory = () => {
  const dateFilter = useDateFilter()
  const { variables } = useVariables()
  const locationGroupIds = variables.allStores?.locationGroupIds
  const startDate = dateFilter.startDate
  const endDate = dateFilter.endDate
  const { data, loading } = useQuery<IInventorySummaryType>(query, {
    variables: {
      iInputParams: {
        locationGroupId: locationGroupIds?.[0],
        startDate,
        endDate,
        otherParams: {
          inventoryType: 'INVENTORY_WEEKLY',
        },
      },
    },
    skip: !startDate || !endDate || !locationGroupIds,
  })
  const bbbInventoryCategories = variables.bbbInventoryCategories?.value[0]?.[0]
  const bbbInventoryCategoriesIsLoading =
    variables.bbbInventoryCategories?.loading

  const formatedData = useMemo(() => {
    const tableData = data?.getCustomizedReport.nodes[0]?.reportResult.tableData

    if (data && !tableData) return []

    if (!tableData || !bbbInventoryCategories) return null

    const formatedData = tableData.map((t) => ({
      ...t,
      id: t.item,
      parentId: 'summary',
      itemName: t.item,
      displayParentCategoryName: t.itemType,
    }))

    return !bbbInventoryCategories ||
      bbbInventoryCategories === 'All Categories'
      ? formatedData
      : formatedData.filter((f) => f.itemType === bbbInventoryCategories)
  }, [data, bbbInventoryCategories])
  const summary = useMemo(() => {
    const summaryData =
      bbbInventoryCategories &&
      data?.getCustomizedReport.nodes[0]?.reportResult.summaryMap[
        bbbInventoryCategories === 'All Categories'
          ? 'ALL'
          : bbbInventoryCategories
      ]

    return {
      ...summaryData,
      id: 'summary',
      parentId: 'root',
      itemName: 'TOTAL',
      displayParentCategoryName: ' ',
    }
  }, [data, bbbInventoryCategories])

  return {
    data: useMemo((): IApiDataType => {
      const searchInput = variables.inputValue?.itemSearch.value as
        | string
        | undefined

      if (!formatedData) return null

      if (formatedData.length === 0) return formatedData

      if (!searchInput) return [...formatedData, summary]

      const searchPattern = searchInput
        .split(';')
        .map((search) => search.trim().toLowerCase())
        .filter(Boolean)
      const allItemNames = formatedData.map((f) => f.item.toLowerCase())
      const matchItems = searchPattern.reduce((result, search) => {
        const bestMatchResults = stringSimilarity.findBestMatch(
          search,
          allItemNames || [],
        )
        const bestMatchRating = bestMatchResults.bestMatch.rating

        if (bestMatchRating < 0.5) return result

        const bestMatchItem = bestMatchResults.bestMatch.target

        return !bestMatchItem || result.includes(bestMatchItem)
          ? result
          : [...result, bestMatchItem]
      }, [] as string[])

      return [
        ...formatedData.filter((n) => {
          const itemStr = n.item.toLowerCase()

          return (
            matchItems.includes(itemStr) ||
            searchPattern.some((s) => itemStr.includes(s))
          )
        }),
        summary,
      ]
    }, [formatedData, summary, variables]),
    loading: loading || bbbInventoryCategoriesIsLoading,
  }
}

export default useBbbCorporateInventory
